import { JSX } from 'solid-js/jsx-runtime';
import {
    Show, Switch, Match, createSignal, onCleanup, createEffect, createMemo, createResource, Setter, For
} from 'solid-js';

import { Context, anyerror } from '@bitiotic/bitiotic';
import { Game, Lobby, Persistent, Round } from '@bitiotic/numbet';

import { DebugObj } from './DebugObj';
import { DebugErr } from './DebugErr';
import { RoundBoard } from './RoundBoard';
import { PlayersList } from './PlayersList';
import { GamePlayerListener, StyledPNum } from './GamePlayerListener';
import { SpinButton, StandardButton } from './Widgets';
import { SectionHeader } from './SectionHeader';
import { PlayerGameWinnings } from '@bitiotic/numbet/src/Game';
import { getGameURL } from './NewGame';

function noopUnsub() { /*noop*/ }

let prevRoundUnsub = noopUnsub;

function JoinDialog(props: {
    ctx: Context,
    currentPlayerPath: Persistent.PlayerPath,
    game: Game,
    setRejection: Setter<unknown>,
}): JSX.Element {

    function registerJoinListener(ctx: Context, game: Game): void {
        const joinCleanup = game.listenForJoinRequest(ctx,
            (playerPath, _reqDoc) => {
                // note that reqDoc is currently ignored because we assume the requesting player
                // is always the primary player.  No secondary players supported.

                ctx.log('join request', playerPath);

                game.addPlayer(ctx, playerPath, true)
                    .catch(err => {
                        ctx.log('Async error on addPlayer:', err);
                        props.setRejection(err);
                    });
            },
            err => props.setRejection(err));
        onCleanup(() => joinCleanup());
    }

    createEffect(() => registerJoinListener(props.ctx.newCtx('joinListen'), props.game));

    // TODO: Actually let the caller see/approve the join request.  Just auto-join for now.
    return <div></div>;
}

function GameCompletedSummary(props: {
    ctx: Context,
    currentPlayerPath: Persistent.PlayerPath,
    game: Game,
    gamePlayers: ReadonlyArray<Persistent.PlayerPath> | null;
    gamePlayerListener: GamePlayerListener,
}): JSX.Element {

    interface PlayerWinnings {
        playerPath: Persistent.PlayerPath,
        winnings: PlayerGameWinnings,
        place: number,
    }

    interface AllPlayerWinnings {
        players: PlayerWinnings[];
    }

    interface WinLevel {
        place: number;
        winnings: number;
        playerPaths: Persistent.PlayerPath[];
    }

    interface WinLevels {
        level: WinLevel[];
    }

    const [getWinnings, setWinnings] = createSignal<Readonly<WinLevels> | null>(null);

    // Convert Game results into a sorted list of PlayerWinnings objects
    createEffect(() => {
        const orderedPlayers: AllPlayerWinnings = {
            players: [],
        };

        /*async*/
        props.game.forEachPlayer(props.ctx, playerPath => {
            return props.game.getPlayerGameWinnings(props.ctx, playerPath).then(winnings => {
                orderedPlayers.players.push({
                    playerPath,
                    winnings,
                    place: 0,
                });
            });
        }).then(() => {
            orderedPlayers.players.sort((a, b) => b.winnings.winnings - a.winnings.winnings);

            // Assign places (same place for ties)
            let place = 0;
            let prevWinnings: number | null = null;
            let wasTied = false;
            for (const entry of orderedPlayers.players) {
                if ((prevWinnings === null) || (entry.winnings.winnings < prevWinnings)) {
                    place = place + 1;
                    if (wasTied) {
                        place = place + 1;
                        wasTied = false;
                    }
                } else {
                    wasTied = true;
                }
                entry.place = place;
                prevWinnings = entry.winnings.winnings;
            }

            const winLevels: WinLevels = {
                level: [],
            };

            for (const player of orderedPlayers.players) {
                const idx = player.place - 1;

                winLevels.level[idx] = winLevels.level[idx] || {
                    place: player.place,
                    winnings: player.winnings.winnings,
                    playerPaths: [],
                };
                props.ctx.assertEqual(winLevels.level[idx].winnings, player.winnings.winnings);
                winLevels.level[idx].playerPaths.push(player.playerPath);
            }

            winLevels.level = winLevels.level.filter(l => l !== undefined);

            setWinnings(winLevels);
        });
    });

    function liClass(place: number): string {
        if (place === 1) {
            return 'text-2xl';
        }
        if (place === 2) {
            return 'text-lg';
        }
        return '';
    }

    function liStyle(players: Persistent.PlayerPath[]): Record<string, string> {
        if (players.includes(props.currentPlayerPath)) {
            return {
                'box-shadow': '0 0 8px 4px #eab308',
            };
        }
        return {};
    }


    function winPrefix(place: number): JSX.Element {
        if (place === 1) {
            return <span>1st place!</span>;
        }
        if (place === 2) {
            return <span>2nd place</span>;
        }
        if (place === 3) {
            return <span>3rd place</span>;
        }
        return <></>;
    }

    return <>
        <SectionHeader ctx={props.ctx}>
            Game Results
        </SectionHeader>
        <div class="m-3 flex flex-col w-fill mx-auto" data-testid="GameResults">
            <Show when={getWinnings() === null}>
                Loading ...
            </Show>
            <Show when={getWinnings() !== null}>
                <For each={getWinnings()!.level}>
                    {level => {
                        return <div
                            class={'mx-auto ' + liClass(level.place)}
                            style={liStyle(level.playerPaths)}>
                            <div class="p-2">
                                {winPrefix(level.place)}&nbsp;
                                <Switch>
                                    <Match when={level.playerPaths.length === 0}>
                                        (nobody)
                                    </Match>
                                    <Match when={level.playerPaths.length === 1}>
                                        <StyledPNum
                                            ctx={props.ctx}
                                            playerPath={level.playerPaths[0]!}
                                            gamePlayerListener={props.gamePlayerListener} />
                                    </Match>
                                    <Match when={level.playerPaths.length === 2}>
                                        <span>Tie!</span>&nbsp;
                                        <StyledPNum
                                            ctx={props.ctx}
                                            playerPath={level.playerPaths[0]!}
                                            gamePlayerListener={props.gamePlayerListener} />
                                        and
                                        <StyledPNum
                                            ctx={props.ctx}
                                            playerPath={level.playerPaths[1]!}
                                            gamePlayerListener={props.gamePlayerListener} />
                                    </Match>
                                    <Match when={level.playerPaths.length > 2}>
                                        <span>Tie!</span>&nbsp;
                                        <For each={level.playerPaths}>
                                            {(pp, _idx) => {
                                                return <StyledPNum
                                                    ctx={props.ctx}
                                                    playerPath={pp}
                                                    gamePlayerListener={props.gamePlayerListener} />;
                                            }}
                                        </For >
                                    </Match >
                                </Switch>
                                &nbsp;won ${level.winnings}
                            </div>
                        </div>;
                    }}
                </For>
            </Show>
        </div>
    </>;
}

export function GameBoard(props: {
    lobby: Lobby,
    game: Game,
    ctx: Context,
    playerPath: 'unknown' | Persistent.PlayerPath
}): JSX.Element {
    if (props.playerPath === 'unknown') {
        return <p>unknown playerpath</p>;
    }

    const lctx = props.ctx.newCtx('gameListen');
    const getGamePlayerListener = createMemo(() => new GamePlayerListener(lctx, props.lobby, props.game, err => {
        // XXX do something better with errors
        throw lctx.chainError(err, 'from GamePlayers listeners');
    }));

    // The "round" object changes too frequently
    // (spammy updates from Firebase because of the schema),
    // so don't rebuild the UI directly from this.
    const [getCurrRound, setCurrRound] = createSignal<Round | null>(null);

    const [getGameState, setGameState] = createSignal<'unknown' | 'running' | 'completed'>('unknown');
    const [getRejection, setRejection] = createSignal<null | unknown>(null);
    const [getGamePlayers, setGamePlayers] = createSignal<Readonly<Persistent.PlayerPath[]> | null>(null);
    const [getWantNextRound, setWantNextRound] = createSignal<boolean>(false);
    const [getNextRoundReady, setNextRoundReady] = createSignal<boolean>(false);

    // Cached signals based on the state of the round (see setNewCurrRound)
    const [getRoundIsUnset, setRoundIsUnset] = createSignal<boolean>(true);
    const [getRoundIsFinished, setRoundIsFinished] = createSignal<boolean>(false);

    // Manager is constant, so no setter needed
    const [getIsManager] = createResource<boolean>(() => {
        if (props.playerPath === 'unknown') {
            return Promise.resolve(false);
        }
        return props.game.isManager(props.ctx, props.playerPath);
    });


    createEffect(() => registerListeners(props.ctx.newCtx('gListen'), props.game,
        Persistent.asPlayerPath(props.playerPath)));

    function registerListeners(ctx: Context, game: Game, playerPath: Persistent.PlayerPath) {
        const initialRound = game.getCurrentRound(ctx); // from cache on Game
        ctx.log('Game', game.getGamePath(), 'completed:', game.isCompleted(), 'curRound:', initialRound);

        setGamePlayers(game.getPlayerListCache(ctx));

        if (game.isCompleted()) {
            ctx.log('gameState for', game.getGamePath(), 'is "completed" at initialization');
            setGameState('completed');
        } else {
            setGameState('running');
        }

        setWantNextRound(true);
        const setNewRnd = setNewCurrRound(ctx, game, initialRound);
        ctx.assertTrue(setNewRnd,
            'Set currRound to', initialRound, 'rejected.');

        const completedCleanup = game.listenForGameCompleted(ctx,
            () => {
                ctx.log('gameState for', game.getGamePath(), 'is "completed"');
                setGameState('completed');
                // XXX explicitly disconnect listeners?  Or let them wind up on their own?
            },
            err => setRejection(err));
        onCleanup(() => completedCleanup());

        const newRoundCleanup = game.listenForNextRound(ctx, playerPath,
            r => {
                ctx.log('new round', r.getRoundPath(ctx), 'in', game.getGamePath());
                setNewCurrRound(ctx, game, r);
            },
            err => setRejection(err)
        );
        onCleanup(() => newRoundCleanup());

        const npCleanup = game.listenForNewPlayers(ctx,
            players => {
                setGamePlayers(players);
            },
            err => {
                ctx.log('Ignoring failure on new player update:', err);
            });
        onCleanup(() => npCleanup());
    }

    // Manager only.  Create the new round and publish it to everyone.
    function makeNextRound(ctx: Context, game: Game, prevRound: Round | null): Promise<unknown> {
        ctx.assertNotNull(prevRound, 'Previous round must be defined');

        setWantNextRound(true);

        return props.game.newRound(ctx, prevRound.getRoundNumber() + 1)
            .then(r => {
                setNewCurrRound(ctx, game, r);
            })
            .catch(err => {
                setRejection(err);
            });
    }

    let delayedRound: Round | null = null; // XXX should be a signal?

    // Non-manager indicates they're ready for the next round (clicked "Join Next Round")
    function readyForNextRound(ctx: Context, game: Game, round: Round | null): void {
        ctx.assertNotNull(round, 'Prevous round must be defined');

        setWantNextRound(true);

        // If we queued up a delayed round update, make that visible now
        if (delayedRound !== null) {
            if (setNewCurrRound(ctx.newCtx('delayed'), game, delayedRound)) {
                delayedRound = null;
            }
        }

    }

    /**
     * Set the given round as the 'Current' round, and figure out what state its in.
     * This Round object comes from the server whenever its ready (i.e., independent
     * of "wantNextRound").
     *
     * Return true if the new round was switched to.
     */
    function setNewCurrRound(ctx: Context, game: Game, round: Round | null): boolean {
        if ((getCurrRound() !== null) && (round !== getCurrRound()) && (getWantNextRound() === false)) {
            /*
             * If current round is at the "results" phase, wait for the user to ACK
             * wanting the next round before installing the new one.
             */
            ctx.log('New round', round?.getRoundPath(ctx), 'delayed while looking at current round results');
            delayedRound = round;
            return false;
        }

        setCurrRound(round);

        prevRoundUnsub();
        prevRoundUnsub = noopUnsub;

        delayedRound = null;
        setRoundIsUnset(round === null);
        setRoundIsFinished(false);
        setNextRoundReady(false);
        setWantNextRound(false);

        if (round !== null) {
            const fin = round.isFinished(ctx) || (getGameState() === 'completed');

            setRoundIsFinished(fin);

            if (!fin) {
                setGameState('running');

                // listen for round to get to be completed
                prevRoundUnsub = round.listenForRoundResults(ctx,
                    () => {
                        setNewCurrRound(ctx, game, round);
                    },
                    err => {
                        if (anyerror.causedByPermissionDenied(err)) {
                            setRejection('Not allowed to see results.');
                        } else {
                            setRejection(err);
                        }
                    });
            }
        }
        return true;
    }

    function assertCurrRound(ctx: Context, r: Round | null): Round {
        ctx.assertNotNull(r, 'Caller should prevent null Round from getting here');
        // ctx.log('assertCurRound: id:', r.instanceId, 'path:', r.getRoundPath(ctx));
        return r;
    }

    function endGameEarly(ctx: Context, game: Game, round: Round | null): Promise<void> {
        ctx.assertNotNull(round, 'Caller should prevent null Round from getting here');

        return game.closeRound(ctx, round).then(() => {
            return game.setGameOver(ctx);
        });
    }

    setRejection(null);

    return <div class="mx-8 md:max-w-3xl">
        <DebugObj
            _closed={true}
            _visible={false}
            playerPath={props.playerPath}
            options={props.game.getOptions()}
            gamePath={props.game.getGamePath()}
            players={getGamePlayers()}
            gameState={getGameState()}
        />
        <Switch>
            <Match when={getGameState() === 'unknown'}>
                Loading Game ...
            </Match>
            <Match when={getGameState() === 'running' || getGameState() === 'completed'}>
                <SectionHeader ctx={props.ctx}>
                    Players
                </SectionHeader>
                <PlayersList
                    ctx={props.ctx.newCtx('plist')}
                    game={props.game}
                    currentPlayerPath={props.playerPath}
                    gamePlayers={getGamePlayers()}
                    round={getCurrRound()}
                    onManagerDevice={getIsManager() || false}
                    gamePlayerListener={getGamePlayerListener()}
                />
                <div>
                    <Show when={getIsManager()}>
                        <Show when={getGameState() !== 'completed'}>
                            <JoinDialog
                                ctx={props.ctx.newCtx('joindialog')}
                                currentPlayerPath={props.playerPath}
                                game={props.game}
                                setRejection={setRejection}
                            ></JoinDialog>
                        </Show>
                    </Show>
                    <Show when={getRoundIsUnset()}>
                        <SectionHeader ctx={props.ctx}>
                            Game
                        </SectionHeader>

                        <Show when={getIsManager()}>
                            <div data-testid="PreRoundManager">
                                <p class="p-2">
                                    Once everyone has joined, you may start the first round.
                                </p>
                                <p class="p-2">
                                    Share <a
                                        class="bg-transparent hover:bg-blue-500 text-blue-700 hover:text-white"
                                        href={getGameURL(props.ctx, props.game.getGamePath())}
                                    >this link</a> to invite others to this game.
                                </p>
                                <SpinButton
                                    onClick={() => {
                                        return props.game.newRound(props.ctx, 0)
                                            .then(() => { });
                                    }}
                                    color='green'
                                >Start First Round</SpinButton>
                            </div>
                        </Show>
                        <Show when={!getIsManager()}>
                            <div data-testid="PreRoundClient">
                                <p class="p-2">
                                    Waiting for Manager to start first round ...
                                </p>
                            </div>
                        </Show>
                        <SectionHeader ctx={props.ctx}>
                            <div data-testid="Results">
                                <div class="text-slate-400">Results</div>
                            </div>
                        </SectionHeader>
                    </Show>
                    <Show when={!getRoundIsUnset()}>
                        <RoundBoard
                            ctx={props.ctx.newCtx('RBrd')}
                            round={assertCurrRound(props.ctx, getCurrRound())}
                            game={props.game}
                            playerPath={props.playerPath}
                            gamePlayerListener={getGamePlayerListener()}
                        />
                        <Show when={getGameState() !== 'completed'}>
                            <DebugObj
                                _visible={false}
                                _closed={true}
                                roundNum={getCurrRound()?.getRoundNumber()}
                                roundPath={getCurrRound()?.getRoundPath(props.ctx)}
                                mgr={getIsManager()}
                                finishedMemo={getRoundIsFinished()}
                                roundIsFinished={getCurrRound()?.isFinished(props.ctx)}
                                roundAction={getCurrRound()?.getPlayerAction(props.ctx, props.playerPath)}
                                wantNextRound={getWantNextRound()}
                                nextRoundReady={getNextRoundReady()}
                                delayedRound_var={delayedRound}
                            />

                            <div class="flex p-3 justify-between w-fill" data-testid="GameControls">
                                <Show when={getIsManager()}>
                                    <Show when={getRoundIsFinished()}>
                                        <SpinButton
                                            onClick={() => {
                                                return makeNextRound(props.ctx, props.game, getCurrRound())
                                                    .then(() => { });
                                            }}
                                            disabled={!getRoundIsFinished()}
                                            color='green'
                                        >Start Next Round</SpinButton>
                                    </Show>
                                    <Show when={!getRoundIsFinished()}>
                                        <div></div>
                                    </Show>
                                    <SpinButton
                                        onClick={() => endGameEarly(props.ctx, props.game, getCurrRound())}
                                        color='red'
                                    >End Game Early</SpinButton>
                                </Show>
                                <Show when={!getIsManager() && getRoundIsFinished()}>
                                    <Show when={getWantNextRound() === true}>
                                        <p class="p-2">
                                            Waiting for Manager to start next Round ...
                                        </p>
                                    </Show>
                                    <Show when={getWantNextRound() === false}>
                                        <StandardButton
                                            onClick={() => readyForNextRound(props.ctx, props.game, getCurrRound())}
                                            disabled={!getRoundIsFinished()}
                                            color='green'
                                        >Join Next Round</StandardButton>
                                    </Show>
                                </Show>
                            </div>
                        </Show>
                    </Show>
                    <Show when={getGameState() === 'completed'}>
                        <GameCompletedSummary
                            ctx={props.ctx.newCtx('complete')}
                            game={props.game}
                            currentPlayerPath={props.playerPath}
                            gamePlayers={getGamePlayers()}
                            gamePlayerListener={getGamePlayerListener()}
                        />
                    </Show>
                </div>
            </Match >
        </Switch >
        <DebugErr label="GameBoard Error" err={getRejection()} />
    </div >;
}
